// Public download page for the Android APK shipped outside Google Play
// (early-beta sideload). Marketed via a footer link on the landing page.
// The APK itself is placed at /sytofood.apk by scripts/copy-apk.mjs at deploy
// time, with the right Content-Disposition headers via public/_headers.

import { type Lang, useLang } from "../lib/i18n";

type Copy = {
  back: string;
  eyebrow: string;
  heroTitle: string;
  heroSub: string;
  download: string;
  meta: (size: string) => string;
  whyTitle: string;
  whyBody: string;
  stepsTitle: string;
  steps: [string, string, string, string];
  disclaimer: string;
  footnote: string;
};

const copy: Record<Lang, Copy> = {
  pl: {
    back: "WRÓĆ",
    eyebrow: "ANDROID · WCZESNA WERSJA",
    heroTitle: "Sytofood na Androida.",
    heroSub: "Aplikacja w wersji beta, dostępna do pobrania bezpośrednio.",
    download: "POBIERZ APK",
    meta: (size) => `app · ${size}`,
    whyTitle: "Dlaczego nie ma jeszcze w Sklepie Play",
    whyBody:
      "Sytofood jest w zamkniętej becie. Sklep wymaga publicznego launchu, my chcemy najpierw dopiąć produkt z pierwszymi użytkownikami. APK instaluje się ręcznie i aktualizuje przez tę samą stronę.",
    stepsTitle: "Jak zainstalować",
    steps: [
      "Pobierz plik APK na telefonie.",
      "Otwórz pobrany plik. Android zapyta o zgodę na instalację z nieznanych źródeł.",
      "Zezwól tylko przeglądarce, z której pobrałeś plik.",
      "Otwórz Sytofood z listy aplikacji. Zaloguj się tym samym kontem co w przeglądarce.",
    ],
    disclaimer:
      "To wczesna wersja. Spodziewaj się zmian, drobnych potknięć i częstych aktualizacji.",
    footnote: "Po stronie webowej nic się nie zmienia, działa nadal w przeglądarce.",
  },
  en: {
    back: "BACK",
    eyebrow: "ANDROID · EARLY BUILD",
    heroTitle: "Sytofood for Android.",
    heroSub: "Beta build, available as a direct download.",
    download: "DOWNLOAD APK",
    meta: (size) => `app · ${size}`,
    whyTitle: "Why not the Play Store yet",
    whyBody:
      "Sytofood is in closed beta. The store wants a public launch; we want to finish the product with early users first. The APK installs by hand and updates from this same page.",
    stepsTitle: "How to install",
    steps: [
      "Download the APK on your phone.",
      "Open the file. Android will ask to allow installs from unknown sources.",
      "Allow it only for the browser you downloaded with.",
      "Open Sytofood from your app drawer. Sign in with the same account you use on the web.",
    ],
    disclaimer: "This is an early build. Expect changes, small rough edges and frequent updates.",
    footnote: "The web version is unchanged and keeps working in your browser.",
  },
  de: {
    back: "ZURÜCK",
    eyebrow: "ANDROID · FRÜHE VERSION",
    heroTitle: "Sytofood für Android.",
    heroSub: "Beta-Version, als direkter Download verfügbar.",
    download: "APK HERUNTERLADEN",
    meta: (size) => `App · ${size}`,
    whyTitle: "Warum noch nicht im Play Store",
    whyBody:
      "Sytofood ist in geschlossener Beta. Der Store will einen öffentlichen Launch; wir wollen das Produkt erst mit frühen Nutzern fertigstellen. Die APK wird manuell installiert und über dieselbe Seite aktualisiert.",
    stepsTitle: "So installierst du",
    steps: [
      "Lade die APK-Datei auf dein Telefon.",
      "Öffne die Datei. Android fragt nach der Erlaubnis, aus unbekannten Quellen zu installieren.",
      "Erlaube es nur für den Browser, mit dem du heruntergeladen hast.",
      "Öffne Sytofood aus deiner App-Liste. Melde dich mit demselben Konto wie im Web an.",
    ],
    disclaimer:
      "Dies ist eine frühe Version. Erwarte Änderungen, kleine Ecken und häufige Updates.",
    footnote: "Die Web-Version bleibt unverändert und funktioniert weiterhin im Browser.",
  },
};

export function AndroidDownloadScreen({ onBack }: { onBack: () => void }) {
  const { lang } = useLang();
  const t = copy[lang];

  return (
    <div className="min-h-dvh" style={{ background: "var(--bg)", color: "var(--ink)" }}>
      <div className="mx-auto max-w-[640px] px-5 pt-6 pb-24">
        <button
          type="button"
          onClick={onBack}
          style={{
            background: "transparent",
            border: "none",
            color: "var(--ink-2)",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.1em",
            cursor: "pointer",
            padding: "8px 0",
          }}
        >
          ← {t.back}
        </button>

        <div
          style={{
            marginTop: 16,
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.14em",
            color: "var(--muted)",
          }}
        >
          {t.eyebrow}
        </div>

        <h1
          style={{
            marginTop: 8,
            fontFamily: "var(--font-display)",
            fontSize: 44,
            fontWeight: 800,
            letterSpacing: "-0.03em",
            lineHeight: 1.02,
          }}
        >
          {t.heroTitle.replace(/\.$/, "")}
          <span style={{ color: "var(--accent)" }}>.</span>
        </h1>

        <p
          style={{
            marginTop: 12,
            fontSize: 16,
            fontWeight: 500,
            lineHeight: 1.45,
            color: "var(--ink-2)",
            maxWidth: 460,
          }}
        >
          {t.heroSub}
        </p>

        <div
          style={{
            marginTop: 26,
            padding: 20,
            borderRadius: 18,
            background: "var(--ink)",
            color: "var(--bg)",
            boxShadow: "0 14px 30px rgba(0,0,0,0.10)",
          }}
        >
          <a
            href="/sytofood.apk"
            download
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: 54,
              borderRadius: 14,
              background: "var(--accent)",
              color: "var(--accent-ink)",
              fontFamily: "var(--font-display)",
              fontSize: 15,
              fontWeight: 800,
              letterSpacing: "0.06em",
              textDecoration: "none",
              boxShadow: "0 8px 24px var(--accent-glow)",
            }}
          >
            {t.download}
          </a>
          <div
            style={{
              marginTop: 10,
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.08em",
              color: "rgba(255,255,255,0.6)",
            }}
          >
            {t.meta("~4 MB")}
          </div>
        </div>

        <section style={{ marginTop: 28 }}>
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 22,
              fontWeight: 800,
              letterSpacing: "-0.02em",
            }}
          >
            {t.stepsTitle}
          </h2>
          <ol style={{ marginTop: 12, paddingLeft: 0, listStyle: "none", display: "grid", gap: 12 }}>
            {t.steps.map((step, idx) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: list is static, order stable
                key={idx}
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                  fontSize: 15,
                  color: "var(--ink-2)",
                  lineHeight: 1.45,
                }}
              >
                <span
                  style={{
                    flex: "0 0 28px",
                    height: 28,
                    borderRadius: 99,
                    background: "var(--surface-2)",
                    color: "var(--ink)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {idx + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </section>

        <section style={{ marginTop: 28 }}>
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 22,
              fontWeight: 800,
              letterSpacing: "-0.02em",
            }}
          >
            {t.whyTitle}
          </h2>
          <p
            style={{
              marginTop: 10,
              fontSize: 15,
              color: "var(--ink-2)",
              lineHeight: 1.55,
              maxWidth: 460,
            }}
          >
            {t.whyBody}
          </p>
        </section>

        <p
          style={{
            marginTop: 28,
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--muted)",
            lineHeight: 1.5,
          }}
        >
          {t.disclaimer}
        </p>
        <p
          style={{
            marginTop: 8,
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--muted)",
            lineHeight: 1.5,
          }}
        >
          {t.footnote}
        </p>
      </div>
    </div>
  );
}
